/* 產品的硬規則，全部寫成純函式 —— 這是唯一能被徹底自動化測試的部分。
   規則只回傳「訊息代碼 ＋ 參數」，不回傳任何文字：所以它與語言無關，測試也不必斷言文案。 */
import type { Category, Concept, InboxItem, KnowledgeLink, ConceptStatus } from "./types.ts";

export const DAY_MS = 86_400_000;
export const SUMMARY_MAX_SENTENCES = 2;
export const SUMMARY_MAX_CHARS = 120;
export const NOTE_MIN_CHARS = 10;
export const LINK_SOFT_CAP = 12;
export const TRASH_RETENTION_DAYS = 30;
export const VERSION_KEEP_COUNT = 20;
export const VERSION_KEEP_DAYS = 30;

/** 領域層擁有的訊息代碼。UI 拿它去查目錄；目錄少一條，TypeScript 當場擋下。 */
export type RuleCode =
  | "error.summary.empty"
  | "error.summary.tooLong"
  | "error.note.tooShort"
  | "error.category.missing"
  | "warn.summary.tooManySentences"
  | "link.error.self"
  | "link.error.duplicate"
  | "link.warn.capReached";

const SENTENCE_SPLIT = /[。！？!?]+|\.(?=\s|$)/g;

export function splitSentences(text: string): string[] {
  const t = text.trim();
  if (!t) return [];
  return t.split(SENTENCE_SPLIT).map((p) => p.trim()).filter((p) => p.length > 0);
}

export function countSentences(text: string): number {
  return splitSentences(text).length;
}

export interface Issue {
  level: "error" | "warning";
  field: string;
  code: RuleCode;
  params?: Record<string, string | number>;
}
export interface ValidationResult { ok: boolean; errors: Issue[]; warnings: Issue[] }

export interface ConceptInput { summary: string; note: string; body?: string; primaryCategoryId: string }

/** 兩句是紀律、標籤是門檻：摘要超長擋下、標籤不足擋下、超過兩句只勸不擋。 */
export function validateConcept(input: ConceptInput): ValidationResult {
  const errors: Issue[] = [];
  const warnings: Issue[] = [];
  const summary = input.summary.trim();
  const note = input.note.trim();

  if (!summary) errors.push({ level: "error", field: "summary", code: "error.summary.empty" });
  if (summary.length > SUMMARY_MAX_CHARS) {
    errors.push({ level: "error", field: "summary", code: "error.summary.tooLong",
      params: { max: SUMMARY_MAX_CHARS, actual: summary.length } });
  }
  const sentences = countSentences(summary);
  if (sentences > SUMMARY_MAX_SENTENCES) {
    warnings.push({ level: "warning", field: "summary", code: "warn.summary.tooManySentences",
      params: { count: sentences } });
  }
  if (note.length < NOTE_MIN_CHARS) {
    errors.push({ level: "error", field: "note", code: "error.note.tooShort",
      params: { min: NOTE_MIN_CHARS, actual: note.length } });
  }
  if (!input.primaryCategoryId) {
    errors.push({ level: "error", field: "primaryCategoryId", code: "error.category.missing" });
  }
  return { ok: errors.length === 0, errors, warnings };
}

/** 圖譜上的節點標籤：未填標題時取摘要前 12 字。 */
export function deriveTitle(summary: string, max = 12): string {
  const t = summary.trim().replace(/\s+/g, " ");
  return t.length <= max ? t : t.slice(0, max);
}

/* ── 到期複習 ── */
export function addDays(base: number, days: number): number { return base + days * DAY_MS }
export function nextReviewDue(now: number, intervalDays: number): number {
  return addDays(now, Math.max(1, Math.round(intervalDays)));
}
export function isOverdue(c: Pick<Concept, "reviewDueAt" | "status">, now: number): boolean {
  if (c.status === "trashed" || c.status === "archived" || c.status === "draft") return false;
  return c.reviewDueAt <= now;
}
export function overdueConcepts(concepts: Concept[], now: number): Concept[] {
  return concepts.filter((c) => isOverdue(c, now)).sort((a, b) => a.reviewDueAt - b.reviewDueAt);
}

/* ── 連結軟上限 ── */
export function linkCountFor(links: KnowledgeLink[], conceptId: string): number {
  return links.filter((l) => l.fromId === conceptId || l.toId === conceptId).length;
}

export interface LinkCheck {
  allowed: boolean; requiresReason: boolean; code: RuleCode | null; params?: Record<string, number>;
}

export function checkNewLink(links: KnowledgeLink[], fromId: string, toId: string, cap = LINK_SOFT_CAP): LinkCheck {
  if (fromId === toId) return { allowed: false, requiresReason: false, code: "link.error.self" };
  const dup = links.some((l) =>
    (l.fromId === fromId && l.toId === toId) || (l.fromId === toId && l.toId === fromId));
  if (dup) return { allowed: false, requiresReason: false, code: "link.error.duplicate" };
  const from = linkCountFor(links, fromId);
  const to = linkCountFor(links, toId);
  if (from >= cap || to >= cap) {
    return { allowed: true, requiresReason: true, code: "link.warn.capReached", params: { cap, from, to } };
  }
  return { allowed: true, requiresReason: false, code: null };
}

/* ── 分級是金鑰邊界，不是顯示開關 ──
   未解鎖時成人知識在「查詢層」就不回傳：連筆數都不給。 */
export interface VisibilityOptions { adultUnlocked: boolean; includeTrashed?: boolean; includeDraft?: boolean }

export function isVisible(c: Concept, o: VisibilityOptions): boolean {
  if (c.nsfw && !o.adultUnlocked) return false;
  if (c.status === "trashed" && !o.includeTrashed) return false;
  if (c.status === "draft" && !o.includeDraft) return false;
  return true;
}

export function visibleConcepts(concepts: Concept[], o: VisibilityOptions): Concept[] {
  return concepts.filter((c) => isVisible(c, o));
}

/* ── 搜尋：中文用 bigram、英文用子字串，欄位加權 ── */
export const SEARCH_WEIGHT: Record<string, number> = { title: 5, summary: 3, note: 2, source: 1, body: 1 };

export function bigrams(s: string): string[] {
  const t = s.replace(/\s+/g, "");
  const out: string[] = [];
  for (let i = 0; i < t.length - 1; i++) out.push(t.slice(i, i + 2));
  if (!out.length && t) out.push(t);
  return out;
}

export function scoreHit(c: Concept, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const grams = bigrams(q);
  const field = (text: string, key: string): number => {
    const lower = text.toLowerCase();
    let hits = 0;
    for (const g of grams) if (lower.includes(g)) hits++;
    if (lower.includes(q)) hits += 2;
    return hits * (SEARCH_WEIGHT[key] ?? 1);
  };
  let score = 0;
  score += field(c.title || deriveTitle(c.summary), "title");
  score += field(c.summary, "summary");
  score += field(c.note, "note");
  score += field(c.source, "source");
  score += field(c.body, "body");
  return score;
}

export interface SearchResult { concept: Concept; score: number }

/** 搜尋一律經過可見性過濾 —— 未解鎖時成人知識連「有幾筆」都不會洩漏。 */
export function searchConcepts(concepts: Concept[], query: string, o: VisibilityOptions): SearchResult[] {
  return visibleConcepts(concepts, o)
    .map((concept) => ({ concept, score: scoreHit(concept, query) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || b.concept.updatedAt - a.concept.updatedAt);
}

/** 托盤的可見性：成人區鎖定時，被標記敏感的素材**完全隱形** ——
    連「有幾包」都不顯示。這是洩漏規則在托盤上的落實。 */
export function visibleInbox(items: InboxItem[], adultUnlocked: boolean): InboxItem[] {
  return adultUnlocked ? items : items.filter((i) => !i.sensitive);
}

/** 敏感的素材一律預設模糊；未掀開的一律連縮圖都不給。 */
export function inboxThumbnailAllowed(item: InboxItem): boolean {
  return item.peeled;
}

/* ── 軟刪除 ── */
export function trashExpired(c: Pick<Concept, "deletedAt">, now: number): boolean {
  return c.deletedAt !== null && now - c.deletedAt > TRASH_RETENTION_DAYS * DAY_MS;
}

export function categoryReviewDefault(categories: Category[], id: string, fallback = 365): number {
  return categories.find((c) => c.id === id)?.reviewDefaultDays ?? fallback;
}

export type StatusCode = "status.draft" | "status.active" | "status.archived" | "status.trashed";
export function statusCode(s: ConceptStatus): StatusCode {
  return ("status." + s) as StatusCode;
}
